import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import isNil from 'lodash/isNil';

import { Spacer, Text } from '@zillow/constellation';
import {
    Col,
    Link,
    Row,
} from '@zillow/zrs-comet';

import Constants from 'app/shared/constants';
import analyticsUtils from 'app/client/utils/analyticsUtils';
import { UserSelectors } from 'app/shared/selectors';
import userUtils from 'app/shared/utils/userUtils';
import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';

import {
    StyledItemDiv,
    StyledListingStatusDiv,
    StyledSpacer,
    StyledStatusDiv,
    StyledText,
} from './StyledComponents';

const propTypes = {
    isCommunity: PropTypes.bool,
    syndication: PropTypes.object.isRequired,
};

const defaultProps = {
    isCommunity: false,
};

class ListingStatus extends React.Component {
    constructor(props) {
        super(props);

        this.handleLinkClick = this.handleLinkClick.bind(this);
        this.hideListingStatus = this.hideListingStatus.bind(this);
        this.renderStatusList = this.renderStatusList.bind(this);
        this.renderUniversalStatusMessage = this.renderUniversalStatusMessage.bind(this);
    }

    handleLinkClick(brand) {
        this.props.dispatch(analyticsUtils.track.GAEventV2(
            AnalyticsConstants.GA_EVENTS.STATUS_LINK_CLICK,
            { action: `View${brand}URL` },
        ));
    }

    renderUniversalStatusMessage() {
        const { syndication } = this.props;
        const { universal } = syndication;

        if (isNil(universal) || !universal.message) {
            return null;
        }
        const { partialMessages } = universal;

        return (
            <Text fontType="body">
                {partialMessages.map((partialMessage, index) => {
                    const { url, text } = partialMessage;
                    if (url) {
                        return (
                            <React.Fragment key={index}>
                                <Link to={url} target="_blank">
                                    {text}
                                </Link>
                            </React.Fragment>
                        );
                    }
                    return (
                        <React.Fragment key={index}>
                            {text}
                        </React.Fragment>
                    );
                })}
            </Text>
        );
    }

    hideListingStatus() {
        const {
            listing: { isEditable, isPaidMultifamily, userRoles },
            canUpdateListing,
        } = this.props;
        const isAdmin = userUtils.isPostingPathAdmin(userRoles);

        return isEditable && canUpdateListing && (!isPaidMultifamily || isAdmin);
    }

    renderStatusList() {
        const { syndication } = this.props;
        const statusList = [
            Constants.UNFORMATTED_BRAND_NAMES.zillow,
            Constants.UNFORMATTED_BRAND_NAMES.trulia,
            Constants.UNFORMATTED_BRAND_NAMES.hotpads,
        ];

        // loop through keys
        return statusList.map((brand) => {
            const { displayStatus, url } = syndication[brand];
            const displayStatusLower = displayStatus.toLowerCase();

            // format for display
            brand = Constants.FORMATTED_BRAND_NAMES[brand];

            return (
                <StyledSpacer paddingY="md" key={brand}>
                    <StyledItemDiv>
                        <Text fontColor="gray500" fontType="smallbody">
                            {brand}
                        </Text>

                        {displayStatusLower === 'posted' ? (
                            <div onClick={() => this.handleLinkClick(brand)}>
                                <Link to={url} target="_blank">
                                    <StyledText fontColor="gray400" itemStatus={displayStatusLower} fontType="body">
                                        {displayStatus}
                                    </StyledText>
                                </Link>
                            </div>
                        ) : (
                            <StyledText fontColor="gray400" itemStatus={displayStatusLower} fontType="body">
                                {displayStatus}
                            </StyledText>
                        )}
                    </StyledItemDiv>
                </StyledSpacer>
            );
        });
    }

    renderListingStatus() {
        const { isCommunity } = this.props;

        const title = 'Listing status';

        // for non community listings
        // the border and title is displayed in ListingOverview component
        const shouldDisplayTitle = isCommunity;
        const shouldDisplayBorderTop = isCommunity;

        const renderStatusList = this.renderStatusList();
        const renderUniversalStatusMessage = this.renderUniversalStatusMessage();

        return (
            <StyledListingStatusDiv className="ListingStatus">
                <Row paddingY="xs">
                    <Col>
                        <div>
                            {shouldDisplayTitle && (
                                <Text
                                    marginBottom="xs"
                                    mediaQuery={{ lg: { marginBottom: 'sm' } }}
                                    fontType="h4"
                                    as="div"
                                >
                                    {title}
                                </Text>
                            )}

                            <StyledStatusDiv
                                className="ListingStatus-StatusContainer"
                                borderTop={shouldDisplayBorderTop}
                                borderBottom={true}
                            >
                                {renderStatusList}
                            </StyledStatusDiv>
                        </div>
                    </Col>
                </Row>
                {renderUniversalStatusMessage && (
                    <Row paddingY="xs">
                        <Col>{renderUniversalStatusMessage}</Col>
                    </Row>
                )}
                <Spacer marginY="md" />
            </StyledListingStatusDiv>
        );
    }

    render() {
        if (this.hideListingStatus()) {
            return (<Spacer marginY="xl" />);
        }
        return this.renderListingStatus();
    }
}

const mapStateToProps = state => ({
    userRoles: UserSelectors.makeRoles(state),
});

ListingStatus.propTypes = propTypes;
ListingStatus.defaultProps = defaultProps;

export { ListingStatus };
export default connect(mapStateToProps)(ListingStatus);
